import React, { useState, useRef } from 'react';
import { X, Send, ShieldCheck, EyeOff, Paperclip, CheckCircle2 } from 'lucide-react';
import TurnstileWidget from './TurnstileWidget';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_ATTACHMENT_BYTES = 100 * 1024 * 1024; // 100 MB
const ALLOWED_TYPES = [
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/gif',
  'video/mp4', 'video/quicktime', 'video/webm',
];

const CATEGORIES = [
  'Gangland',
  'Tobacco War',
  'Drug Network',
  'AFL Scandal',
  'Corruption',
  'Crime Scene',
  'Other',
];

const ConfettiCheck = () => (
  <div className="relative inline-flex h-16 w-16 items-center justify-center" aria-hidden="true">
    <div className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping-once" />
    <CheckCircle2 className="relative h-10 w-10 text-emerald-400 animate-pop-in" />
    {[...Array(8)].map((_, i) => (
      <span
        key={i}
        className="absolute w-1.5 h-1.5 rounded-full animate-spark"
        style={{
          background: ['#fbbf24', '#34d399', '#f87171', '#60a5fa'][i % 4],
          transform: `rotate(${i * 45}deg) translateY(-32px)`,
          animationDelay: `${i * 30}ms`,
        }}
      />
    ))}
    <style>{`
      @keyframes ping-once { 0% { transform: scale(0.6); opacity: 0.9; } 100% { transform: scale(2); opacity: 0; } }
      @keyframes pop-in    { 0% { transform: scale(0); opacity: 0; } 70% { transform: scale(1.15); opacity: 1; } 100% { transform: scale(1); } }
      @keyframes spark     { 0% { opacity: 0; transform: rotate(var(--rot,0deg)) translateY(-10px) scale(0.6); } 50% { opacity: 1; } 100% { opacity: 0; transform: rotate(var(--rot,0deg)) translateY(-48px) scale(1); } }
      .animate-ping-once { animation: ping-once 0.7s ease-out forwards; }
      .animate-pop-in   { animation: pop-in 0.45s cubic-bezier(.34,1.6,.64,1) forwards; }
      .animate-spark    { animation: spark 0.9s ease-out forwards; }
    `}</style>
  </div>
);

/**
 * The reusable Tip submission form.
 *  - Standalone page mode: `compact={false}` (default) - full chrome with header
 *  - Modal mode:           `compact={true}` - caller renders its own close button
 *
 * `onDone` (optional) is called after a successful send, useful for closing modal.
 */
const TipForm = ({ compact = false, onDone, successCtaLabel = 'Close' }) => {
  const [tipText, setTipText] = useState('');
  const [alias, setAlias] = useState('');
  const [category, setCategory] = useState('Gangland');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [errorMsg, setErrorMsg] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [website, setWebsite] = useState(''); // honeypot
  const fileInputRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    if (!ALLOWED_TYPES.includes(f.type)) {
      setStatus('error');
      setErrorMsg('Unsupported file type. Use JPG, PNG, WebP, HEIC, GIF, MP4, MOV, or WebM.');
      return;
    }
    if (f.size > MAX_ATTACHMENT_BYTES) {
      setStatus('error');
      setErrorMsg('File is too large. Max 100 MB.');
      return;
    }
    setStatus(null);
    setErrorMsg('');
    setFile(f);
  };

  const uploadAttachment = async () => {
    if (!file) return null;
    const presignRes = await fetch(`${API}/api/tips/presign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({ filename: file.name, content_type: file.type }),
    });
    if (!presignRes.ok) {
      const data = await presignRes.json().catch(() => ({}));
      throw new Error(data.detail || 'Could not prepare upload.');
    }
    const { upload_url, public_url } = await presignRes.json();
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', upload_url);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error('Upload failed.'));
      xhr.onerror = () => reject(new Error('Upload network error.'));
      xhr.send(file);
    });
    return public_url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setErrorMsg('');
    if (tipText.trim().length < 8) {
      setStatus('error');
      setErrorMsg('Tip must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const attachmentUrl = file ? await uploadAttachment() : null;

      // Client-side device profile - sent alongside the tip so admin has
      // a full forensic record on file for any law-enforcement request.
      // All values come from public browser APIs that any website can read.
      const nav = window.navigator || {};
      const scr = window.screen || {};
      let tz = '';
      try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { /* ignore */ }
      const client_profile = {
        user_agent: nav.userAgent || '',
        platform: nav.platform || '',
        language: nav.language || '',
        languages: (nav.languages || []).slice(0, 4).join(', '),
        cores: nav.hardwareConcurrency || 0,
        memory: nav.deviceMemory || 0,
        touch_points: nav.maxTouchPoints || 0,
        screen: `${scr.width || 0}x${scr.height || 0}`,
        color_depth: scr.colorDepth || 0,
        pixel_ratio: window.devicePixelRatio || 1,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        timezone: tz,
        tz_offset: new Date().getTimezoneOffset(),
        vendor: nav.vendor || '',
        online: nav.onLine ? 'yes' : 'no',
        do_not_track: nav.doNotTrack === '1' ? 'enabled' : 'disabled',
        cookies_enabled: nav.cookieEnabled ? 'yes' : 'no',
        submitted_at_local: new Date().toString(),
      };

      const res = await fetch(`${API}/api/tips/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({
          tip_text: tipText.trim(),
          alias: alias.trim() || null,
          category,
          attachment_url: attachmentUrl,
          client_profile,
          turnstile_token: turnstileToken,
          website,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Could not send tip. Please try again.');
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Could not send tip. Please try again.');
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className={compact ? '' : 'w-full'}>
      {!compact && (
        <div className="flex items-center gap-2 mb-1">
          <EyeOff className="h-6 w-6 text-red-500" />
          <h1 className="text-2xl sm:text-3xl font-black text-white">SUBMIT A TIP</h1>
        </div>
      )}
      {compact && (
        <div className="flex items-center gap-2 mb-1">
          <EyeOff className="h-5 w-5 text-red-500" />
          <h2 className="text-xl sm:text-2xl font-black text-white">SUBMIT A TIP</h2>
        </div>
      )}
      <p className="text-xs sm:text-sm text-gray-400 mb-4">
        Got something the public should know? Tell us. We protect our sources.
      </p>

      <div
        className="bg-emerald-950/40 border border-emerald-500/40 rounded-lg px-3 py-2.5 mb-5 flex items-start gap-2.5"
        data-testid="tip-form-privacy-notice"
      >
        <ShieldCheck className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs sm:text-sm text-emerald-100/90 leading-snug">
          <span className="text-emerald-400 font-black">FOR YOUR OWN SAFETY:</span>{' '}
          No IP address or device tracking applies to any tips made via this page. Your
          submission is fully anonymous unless you choose to identify yourself.
        </p>
      </div>

      {status === 'success' ? (
        <div className="text-center py-6" data-testid="tip-form-success">
          <ConfettiCheck />
          <p className="mt-4 text-lg font-black text-white">Tip received.</p>
          <p className="text-sm text-gray-400 mt-1">Thanks for the intel. We&apos;ll take it from here.</p>
          {onDone && (
            <button
              type="button"
              onClick={onDone}
              className="mt-5 inline-flex items-center justify-center px-5 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-sm transition-colors"
            >
              {successCtaLabel}
            </button>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="tip-form">
          {status === 'error' && (
            <div
              className="bg-red-950/40 border border-red-500/50 text-red-200 px-3 py-2 rounded text-sm"
              data-testid="tip-form-error"
            >
              {errorMsg}
            </div>
          )}
          <div>
            <label className="block text-white text-xs font-bold mb-1 uppercase tracking-wider">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="tip-form-category"
              className="w-full bg-black/70 border border-zinc-700 focus:border-red-500 text-white px-3 py-2.5 rounded text-sm focus:outline-none"
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-white text-xs font-bold mb-1 uppercase tracking-wider">
              Alias <span className="text-gray-500 lowercase font-normal normal-case">(optional)</span>
            </label>
            <input
              type="text"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              data-testid="tip-form-alias"
              placeholder="Leave blank to stay fully anonymous"
              maxLength={80}
              className="w-full bg-black/70 border border-zinc-700 focus:border-red-500 text-white px-3 py-2.5 rounded text-sm focus:outline-none placeholder-gray-600"
            />
          </div>
          <div>
            <label className="block text-white text-xs font-bold mb-1 uppercase tracking-wider">Your tip</label>
            <textarea
              value={tipText}
              onChange={(e) => setTipText(e.target.value)}
              data-testid="tip-form-text"
              placeholder="What's going down? Names, locations, dates - anything you've got."
              rows={6}
              maxLength={10000}
              required
              className="w-full bg-black/70 border border-zinc-700 focus:border-red-500 text-white px-3 py-2.5 rounded text-sm focus:outline-none placeholder-gray-600 resize-y"
            />
            <p className="mt-1 text-[11px] text-gray-500">{tipText.length} / 10,000 characters</p>
          </div>
          <div>
            <label className="block text-white text-xs font-bold mb-1 uppercase tracking-wider">
              Attach Photo or Video <span className="text-gray-500 lowercase font-normal normal-case">(optional)</span>
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_TYPES.join(',')}
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="tip-form-file-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="tip-form-file-btn"
                className="flex items-center gap-2 px-3 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-bold border border-zinc-700"
              >
                <Paperclip className="h-4 w-4" />
                {file ? 'Change File' : 'Choose File'}
              </button>
              {file && (
                <div className="flex-1 min-w-0 text-xs text-gray-300 truncate">
                  {file.name} <span className="text-gray-500">({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
                </div>
              )}
              {file && (
                <button
                  type="button"
                  onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="text-gray-400 hover:text-white"
                  aria-label="Remove attachment"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className="text-[11px] text-gray-500 mt-1">Max 100 MB. Stored anonymously alongside your tip.</p>
            {submitting && file && uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-2">
                <p className="text-[11px] text-gray-400 mb-1">Uploading… {uploadProgress}%</p>
                <div className="w-full h-1.5 bg-zinc-800 rounded overflow-hidden">
                  <div className="h-full bg-red-500 transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}
          </div>
          {/* Honeypot - hidden from real users. Bots fill it. */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
          />
          {/* Cloudflare Turnstile - invisible when the visitor looks trusted */}
          <div className="flex justify-center">
            <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken(null)} />
          </div>
          <button
            type="submit"
            disabled={submitting || tipText.trim().length < 8}
            data-testid="tip-form-submit-btn"
            className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed text-white font-black py-3 px-4 rounded transition-colors uppercase tracking-wider text-sm"
          >
            <Send className="h-4 w-4" />
            {submitting ? 'Sending…' : 'Send Tip'}
          </button>
        </form>
      )}
    </div>
  );
};

export default TipForm;
